import React, { useState, useEffect } from 'react';
import Portal from '../common/Portal';
import CustomSelect from '../common/CustomSelect';
import DatePicker from 'react-datepicker';

// A standard gestation period for cattle is ~283 days.
const GESTATION_DAYS = 284;

const BreedingEventModal = ({ isOpen, onClose, onSubmit, isLoading, livestock }) => {
    
    const getInitialState = () => ({
        animalId: '',
        eventType: 'Natural Mating',
        eventDate: new Date().toISOString().split('T')[0],
        sire: '', // Father's name or tag
        status: 'In-Progress',
    });

    const [eventData, setEventData] = useState(getInitialState());

    useEffect(() => {
        if (isOpen) {
            setEventData(getInitialState());
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        setEventData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleAnimalChange = (selectedValue) => {
        setEventData(prev => ({ ...prev, animalId: selectedValue }));
    };

    const handleDateChange = (date) => {
        const formattedDate = date ? date.toISOString().split('T')[0] : '';
        setEventData(prev => ({ ...prev, eventDate: formattedDate }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Calculate the due date before submitting
        const eventDateObj = new Date(eventData.eventDate);
        eventDateObj.setDate(eventDateObj.getDate() + GESTATION_DAYS);
        const dueDate = eventDateObj.toISOString().split('T')[0];

        onSubmit({ ...eventData, dueDate });
    };

    // Prepare options for the animal selection dropdown
    const animalOptions = livestock
        .map(animal => ({
            id: animal.id,
            name: `${animal.name} (${animal.tagNumber})`,
            value: animal.id,
        }));
    
    const selectedDate = eventData.eventDate ? new Date(eventData.eventDate) : null;

    return (
        <Portal>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
                <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md m-4">
                    <h2 className="text-2xl font-bold mb-6 text-gray-800">Record New Breeding Event</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Select Animal</label>
                            <CustomSelect
                                options={animalOptions}
                                selected={eventData.animalId}
                                onChange={handleAnimalChange}
                                placeholder="Choose an animal..."
                            />
                        </div>

                        <div>
                            <label htmlFor="eventType" className="block text-sm font-medium text-gray-700">Event Type</label>
                            <select name="eventType" id="eventType" value={eventData.eventType} onChange={handleChange} className="mt-1 block w-full form-select rounded-md shadow-sm">
                                <option>Natural Mating</option>
                                <option>Artificial Insemination (AI)</option>
                                <option>Embryo Transfer</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">Event Date</label>
                            <DatePicker
                                selected={selectedDate}
                                onChange={handleDateChange}
                                dateFormat="yyyy-MM-dd"
                                className="mt-1 block w-full form-input rounded-md shadow-sm"
                                wrapperClassName="w-full"
                                maxDate={new Date()}
                                portalId="portal-root"
                                // This is the fix: apply our high-level z-index class
                                popperClassName="high-z-datepicker"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="sire" className="block text-sm font-medium text-gray-700">Sire (Father)</label>
                            <input type="text" name="sire" id="sire" value={eventData.sire} onChange={handleChange} placeholder="Enter Sire name or tag #" className="mt-1 block w-full form-input rounded-md shadow-sm" required />
                        </div>

                        <div className="pt-5 flex justify-end space-x-3">
                            <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
                            <button type="submit" disabled={isLoading} className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-indigo-300 flex items-center">
                                {isLoading ? 'Saving...' : 'Save Event'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </Portal>
    );
};

export default BreedingEventModal;